import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompanySettings } from '../contexts/CompanySettingsContext';
import { useAuth } from '../contexts/AuthContext';
import { 
  Box, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Paper, 
  Grid,
  CircularProgress,
  Alert
} from '@mui/material';
import { 
  PieChart, 
  Pie, 
  Cell, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/tr';

const COLORS = ['#0088FE', '#FFBB28', '#FF8042', '#00C49F', '#FF6B6B', '#4ECDC4'];

const ReportsContent = ({ user }) => {
  const [timeRange, setTimeRange] = useState('this_month');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    employeeStatus: [],
    departmentStats: [],
    attendanceStats: { present: 0, absent: 0, late: 0 },
    leaveStats: { approved: 0, pending: 0, rejected: 0 }
  });

  const getDateRange = () => {
    const now = moment();
    let startDate, endDate;
    
    switch (timeRange) {
      case 'this_week':
        startDate = now.clone().startOf('week');
        endDate = now.clone().endOf('week');
        break;
      case 'last_month':
        startDate = now.clone().subtract(1, 'months').startOf('month');
        endDate = now.clone().subtract(1, 'months').endOf('month');
        break;
      case 'this_month':
      default:
        startDate = now.clone().startOf('month');
        endDate = now.clone().endOf('month');
    }

    return {
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD')
    };
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { startDate, endDate } = getDateRange();
      
      const api = axios.create({
        baseURL: '/api',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      api.interceptors.response.use(
        response => response,
        error => {
          console.error('API Error:', error.response || error);
          return Promise.reject(error);
        }
      );
      
      const endpoints = [
        { key: 'employees', url: '/employees' },
        { key: 'departments', url: '/departments' },
        { 
          key: 'attendance', 
          url: `/attendance`,
          params: { start_date: startDate, end_date: endDate }
        },
        { 
          key: 'leave-requests', 
          url: '/leave-requests',
          params: { status: 'all', start_date: startDate, end_date: endDate }
        }
      ];

      const responses = await Promise.allSettled(
        endpoints.map(endpoint => 
          api.get(endpoint.url, { params: endpoint.params }).catch(error => ({
            status: 'rejected',
            reason: error,
            endpoint: endpoint.key
          }))
        )
      );

      const failedRequests = responses.filter(r => r.status === 'rejected');
      if (failedRequests.length > 0) {
        console.error('Failed API requests:', failedRequests);
        throw new Error(`Failed to fetch data from ${failedRequests.length} endpoints`);
      }

      const [employeesRes, departmentsRes, attendanceRes, leavesRes] = responses.map(r => r.value);

      const employees = employeesRes?.data || [];
      const activeEmployees = employees.filter(emp => emp.status === 'Aktif').length;
      const inactiveEmployees = employees.length - activeEmployees;
      
      const departments = departmentsRes?.data || [];
      const departmentStats = departments.map(dept => ({
        name: dept.name || 'Belirtilmemiş',
        employees: employees.filter(emp => emp.department_id === dept.id).length
      }));

      const attendanceData = attendanceRes?.data || [];
      const presentCount = attendanceData.filter(a => a.status === 'present' || a.status === 'Present' || a.status === 'PRESENT').length;
      const absentCount = attendanceData.filter(a => a.status === 'absent' || a.status === 'Absent' || a.status === 'ABSENT').length;
      const lateCount = attendanceData.filter(a => a.status === 'late' || a.status === 'Late' || a.status === 'LATE').length;

      const leavesData = leavesRes?.data || [];
      const approvedLeaves = leavesData.filter(l => 
        l.status === 'approved' || l.status === 'Approved' || l.status === 'APPROVED'
      ).length;
      const pendingLeaves = leavesData.filter(l => 
        l.status === 'pending' || l.status === 'Pending' || l.status === 'PENDING' || !l.status
      ).length;
      const rejectedLeaves = leavesData.filter(l => 
        l.status === 'rejected' || l.status === 'Rejected' || l.status === 'REJECTED'
      ).length;

      setStats({
        employeeStatus: [
          { name: 'Aktif', value: activeEmployees },
          { name: 'Pasif', value: inactiveEmployees }
        ],
        departmentStats: departmentStats.filter(dept => dept.employees > 0),
        attendanceStats: {
          present: presentCount,
          absent: absentCount,
          late: lateCount
        },
        leaveStats: {
          approved: approvedLeaves,
          pending: pendingLeaves,
          rejected: rejectedLeaves
        }
      });
    } catch (err) {
      console.error('Error in fetchData:', {
        message: err.message,
        stack: err.stack,
        response: err.response?.data
      });
      setError(`Veri yüklenirken bir hata oluştu: ${err.message}. Lütfen konsolu kontrol edin ve daha sonra tekrar deneyiniz.`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [timeRange]);

  const attendanceData = [
    { name: 'Mevcut', value: stats.attendanceStats.present },
    { name: 'Eksik', value: stats.attendanceStats.absent },
    { name: 'Geç', value: stats.attendanceStats.late }
  ];

  const leaveData = [
    { name: 'Onaylandı', value: stats.leaveStats.approved },
    { name: 'Bekliyor', value: stats.leaveStats.pending },
    { name: 'Reddedildi', value: stats.leaveStats.rejected }
  ];

  // Return the JSX for the reports content
  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Raporlar</Typography>
        <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Zaman Aralığı</InputLabel>
          <Select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            label="Zaman Aralığı"
          >
            <MenuItem value="this_week">Bu Hafta</MenuItem>
            <MenuItem value="this_month">Bu Ay</MenuItem>
            <MenuItem value="last_month">Geçen Ay</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      ) : (
        <Grid container spacing={3}>
          {/* Employee Status */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>Personel Durumu</Typography>
              <Box height={300}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={stats.employeeStatus}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {stats.employeeStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => [`${value} kişi`, '']}
                      labelFormatter={(name) => `${name}`}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          {/* Department Distribution */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>Departman Dağılımı</Typography>
              <Box height={300}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={stats.departmentStats}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={100} />
                    <Tooltip 
                      formatter={(value) => [`${value} kişi`, '']}
                    />
                    <Bar dataKey="employees" fill="#8884d8" name="Çalışan Sayısı" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          {/* Attendance Stats */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>Devam Durumu</Typography>
              <Box height={300}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={attendanceData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip 
                      formatter={(value) => [`${value} kişi`, '']}
                    />
                    <Bar dataKey="value" fill="#82ca9d" name="Kişi Sayısı" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          {/* Leave Stats */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>İzin Durumu</Typography>
              <Box height={300}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={leaveData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {leaveData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[(index + 2) % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => [`${value} izin`, '']}
                      labelFormatter={(name) => `${name}`}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

const Reports = () => {
  const { canAccessReports } = useCompanySettings();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Handle navigation if user doesn't have access
  useEffect(() => {
    if (!canAccessReports) {
      navigate('/');
    }
  }, [canAccessReports, navigate]);

  // Show nothing while checking access or if no access
  if (!canAccessReports) {
    return null;
  }

  return <ReportsContent user={user} />;
};

export default Reports;
